import React, { useState, useEffect } from 'react';
import { XMarkIcon, ArrowUpTrayIcon, DocumentArrowDownIcon, CheckCircleIcon, XCircleIcon, ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import api, { templateAPI } from '../../services/api';
import SearchableDropdown from '../common/SearchableDropdown';
import Papa from 'papaparse';

// PROMPT-043-FIX-09: 3-Step Bulk Upload Wizard with Field Mapping

const BulkUploadWizard = ({ isOpen, onClose, onSuccess }) => {
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: File upload
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Entity selection (from FIX-07)
  const [userRole, setUserRole] = useState('');
  const [entities, setEntities] = useState([]); // Always initialize as empty array
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [selectedEntityName, setSelectedEntityName] = useState('');
  const [loadingEntities, setLoadingEntities] = useState(false);
  
  // Step 2: Parsed data
  const [uploadId, setUploadId] = useState('');
  const [fileFields, setFileFields] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [expiresIn, setExpiresIn] = useState(0);
  
  // Step 2: Field mapping
  const [fieldMapping, setFieldMapping] = useState({
    header: '',
    template_id: '',
    template_name: '',
    template_type: '',
    template_content: '',
    status: ''
  });
  
  // Step 3: Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');

  // System required fields
  const systemFields = [
    { key: 'header', label: 'Header', required: true },
    { key: 'template_id', label: 'Template ID (DLT)', required: true },
    { key: 'template_name', label: 'Template Name', required: true },
    { key: 'template_type', label: 'Template Type', required: true },
    { key: 'template_content', label: 'Template Content', required: true },
    { key: 'status', label: 'Status', required: false }
  ];

  // Fetch user role and entities on mount
  useEffect(() => {
    if (isOpen) {
      fetchUserRoleAndEntities();
    }
  }, [isOpen]);

  const fetchUserRoleAndEntities = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
      const role = userInfo.role || '';
      setUserRole(role);

      setLoadingEntities(true);
      
      // Use admin endpoint for admin/master_admin roles, user endpoint for regular users
      let entitiesData = [];
      if (role === 'master_admin' || role === 'MASTER_ADMIN' || role === 'admin' || role === 'ADMIN') {
        try {
          const response = await api.get('/admin/entities');
          // Admin endpoint returns { entities: [...] } or array directly
          entitiesData = response.data?.entities || response.data?.data || response.data || [];
          if (!Array.isArray(entitiesData)) entitiesData = [];
        } catch (adminErr) {
          console.warn('Admin entities failed, trying user endpoint:', adminErr);
          const response = await api.get('/user/entities');
          entitiesData = response.data?.entities || response.data || [];
          if (!Array.isArray(entitiesData)) entitiesData = [];
        }
      } else {
        const response = await api.get('/user/entities');
        entitiesData = response.data?.entities || response.data || [];
        if (!Array.isArray(entitiesData)) entitiesData = [];
      }
      
      setEntities(entitiesData);
    } catch (err) {
      console.error('Failed to fetch entities:', err);
      setEntities([]); // Set empty array on error
    } finally {
      setLoadingEntities(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(csv)$/i)) {
        setError('Please upload a CSV file');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await templateAPI.downloadSampleCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_upload_sample.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download sample CSV');
    }
  };

  const handleParseFile = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    if (!selectedEntityId) {
      setError('Please select an entity');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // PROMPT-214: Client-side CSV validation before sending to server
      console.log('Starting client-side CSV validation...');
      
      // Parse CSV on client-side first to catch format issues early
      const parseResult = await new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: false,
          transformHeader: h => h.trim(),
          complete: (results) => {
            console.log('CSV Parse Success:', {
              dataLength: results.data?.length || 0,
              errors: results.errors,
              meta: results.meta
            });
            
            if (!results.data || results.data.length === 0) {
              reject(new Error("CSV has no data rows"));
              return;
            }
            
            // Check for parse errors
            if (results.errors && results.errors.length > 0) {
              const errorMessages = results.errors.map(err => `Row ${err.row}: ${err.message}`).join('; ');
              reject(new Error(`CSV parsing error: ${errorMessages}`));
              return;
            }
            
            resolve(results);
          },
          error: (err) => {
            console.error("CSV Parse Error:", err);
            reject(new Error(`Invalid CSV format: ${err.message}`));
          }
        });
      });

      // Validate CSV structure
      const csvData = parseResult.data;
      const csvHeaders = parseResult.meta.fields;
      
      console.log('CSV Headers:', csvHeaders);
      console.log('Sample Data (first 3 rows):', csvData.slice(0, 3));
      
      // Check for minimum required columns (will be validated during mapping)
      if (csvHeaders.length < 5) {
        throw new Error('CSV must have at least 5 columns for template data');
      }

      // If client-side validation passes, send to server
      console.log('Client-side validation passed, sending to server...');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_id', selectedEntityId);

      const response = await templateAPI.parseBulkUploadFile(formData);
      
      setUploadId(response.data.upload_id);
      setFileFields(response.data.file_fields || []);
      setSampleRows(response.data.sample_rows || []);
      setTotalRows(response.data.total_rows || 0);
      setExpiresIn(response.data.expires_in || 600);
      
      // Auto-map fields if names match
      autoMapFields(response.data.file_fields || []);
      
      setCurrentStep(2);
    } catch (err) {
      console.error('Parse error:', err);
      
      // Handle different error types
      if (err.response) {
        const status = err.response.status;
        if (status === 404) {
          setError('Parse API not found. Please contact administrator.');
        } else if (status === 403) {
          setError('You do not have permission to upload templates.');
        } else if (status === 400) {
          setError(err.response.data?.error || 'Invalid file. Please check your CSV format.');
        } else {
          setError(err.response.data?.error || 'Failed to parse file');
        }
      } else if (err.message && err.message.includes('CSV')) {
        // Client-side CSV parsing errors
        setError(`CSV format error: ${err.message}`);
      } else {
        setError('Failed to parse file. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const autoMapFields = (fields) => {
    const mapping = {};
    const lowerFields = fields.map(f => f.toLowerCase());
    
    systemFields.forEach(sf => {
      const variations = [
        sf.key,
        sf.key.replace('_', ' '),
        sf.label.toLowerCase(),
        sf.label.toLowerCase().replace(' ', '_')
      ];
      
      for (const variation of variations) {
        const idx = lowerFields.indexOf(variation);
        if (idx !== -1) {
          mapping[sf.key] = fields[idx];
          break;
        }
      }
    });
    
    setFieldMapping(prev => ({ ...prev, ...mapping }));
  };

  const handleImport = async () => {
    // Validate required fields are mapped
    const missingFields = systemFields
      .filter(sf => sf.required && !fieldMapping[sf.key])
      .map(sf => sf.label);
    
    if (missingFields.length > 0) {
      setError(`Please map required fields: ${missingFields.join(', ')}`);
      return;
    }

    setImporting(true);
    setError('');

    try {
      const response = await templateAPI.importUpload({
        upload_id: uploadId,
        field_mapping: fieldMapping
      });
      
      setImportResult(response.data);
      
      if (response.data.success_count > 0) {
        onSuccess();
      }
      
      setCurrentStep(3);
    } catch (err) {
      if (err.response) {
        const status = err.response.status;
        const errorData = err.response.data;
        const referenceNumber = errorData?.reference_number;
        
        if (status === 404) {
          setError('Upload session expired. Please start over.');
        } else if (status === 403) {
          setError('You do not have permission to import templates.');
        } else if (status === 400) {
          const errorMessage = errorData?.error || 'Invalid field mapping.';
          if (referenceNumber) {
            setError(`Unable to process template.\nReference No: ${referenceNumber}\nPlease contact support with this reference.`);
          } else {
            setError(errorMessage);
          }
        } else {
          const errorMessage = errorData?.error || 'Failed to import templates';
          if (referenceNumber) {
            setError(`Unable to process template.\nReference No: ${referenceNumber}\nPlease contact support with this reference.`);
          } else {
            setError(errorMessage);
          }
        }
      } else {
        setError('Failed to import templates. Please try again.');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleCancel = async () => {
    if (uploadId) {
      try {
        await templateAPI.cancelUpload(uploadId);
      } catch (err) {
        console.error('Failed to cancel upload:', err);
      }
    }
    handleClose();
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFile(null);
    setUploadId('');
    setFileFields([]);
    setSampleRows([]);
    setTotalRows(0);
    setFieldMapping({
      header: '',
      template_id: '',
      template_name: '',
      template_type: '',
      template_content: '',
      status: ''
    });
    setImportResult(null);
    setError('');
    // PROMPT-214: Don't reset entity selection when closing
    // This allows users to keep their entity selection if they reopen the modal
    // setSelectedEntityId('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Bulk Upload Templates</h2>
            <p className="text-sm text-gray-600 mt-1">Step {currentStep} of 3</p>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-indigo-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-300'}`}>
                1
              </div>
              <span className="ml-2 font-medium">Upload File</span>
            </div>
            <div className="flex-1 h-1 mx-4 bg-gray-300">
              <div className={`h-full ${currentStep >= 2 ? 'bg-indigo-600' : 'bg-gray-300'}`} style={{ width: currentStep >= 2 ? '100%' : '0%' }}></div>
            </div>
            <div className={`flex items-center ${currentStep >= 2 ? 'text-indigo-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-300'}`}>
                2
              </div>
              <span className="ml-2 font-medium">Map Fields</span>
            </div>
            <div className="flex-1 h-1 mx-4 bg-gray-300">
              <div className={`h-full ${currentStep >= 3 ? 'bg-indigo-600' : 'bg-gray-300'}`} style={{ width: currentStep >= 3 ? '100%' : '0%' }}></div>
            </div>
            <div className={`flex items-center ${currentStep >= 3 ? 'text-indigo-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-indigo-600 text-white' : 'bg-gray-300'}`}>
                3
              </div>
              <span className="ml-2 font-medium">Import</span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Upload File */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Entity Selection */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Entity Name *
                </label>
                
                {loadingEntities ? (
                  <div className="flex items-center gap-2 text-gray-600 py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                    <span className="text-sm">Loading entities...</span>
                  </div>
                ) : (
                  <div>
                    <SearchableDropdown
                      options={(Array.isArray(entities) ? entities : []).map((entity) => ({
                        value: entity.id.toString(),
                        label: entity.entity_name || entity.name
                      }))}
                      value={selectedEntityId}
                      onChange={(value) => {
                        setSelectedEntityId(value);
                        // Also set the entity name for reference
                        const selected = entities.find(e => e.id.toString() === value);
                        if (selected) {
                          setSelectedEntityName(selected.entity_name || selected.name);
                        }
                      }}
                      placeholder="Select Entity"
                      clearable={false}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Selected entity will be applied to all templates
                    </p>
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Instructions</h3>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Download the sample CSV file to see the required format</li>
                  <li>Upload your CSV file with template data</li>
                  <li>Next step: Map your file columns to system fields</li>
                  <li>Final step: Review and import templates</li>
                </ul>
              </div>

              {/* Download Sample */}
              <div>
                <button
                  onClick={handleDownloadSample}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  <DocumentArrowDownIcon className="w-5 h-5" />
                  Download Sample CSV
                </button>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File *
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                {file && (
                  <p className="mt-2 text-sm text-gray-600">
                    Selected: {file.name}
                  </p>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">Error</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Map Fields */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Session Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">File Parsed Successfully</h3>
                <div className="text-sm text-blue-800 space-y-1">
                  <p>Total rows: <strong>{totalRows}</strong></p>
                  <p>Session expires in: <strong>{Math.floor(expiresIn / 60)} minutes</strong></p>
                </div>
              </div>

              {/* Field Mapping Table */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Map File Columns to System Fields</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          System Field
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          File Column
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {systemFields.map((field) => (
                        <tr key={field.key}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {field.label} {field.required && <span className="text-red-600">*</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <select
                              value={fieldMapping[field.key]}
                              onChange={(e) => setFieldMapping(prev => ({ ...prev, [field.key]: e.target.value }))}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                              <option value="">-- Select Column --</option>
                              {fileFields.map((fileField, idx) => (
                                <option key={idx} value={fileField}>
                                  {fileField}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Sample Data Preview */}
              {sampleRows.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Sample Data Preview</h3>
                  <div className="border border-gray-200 rounded-lg overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {fileFields.map((field, idx) => (
                            <th key={idx} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {field}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sampleRows.map((row, rowIdx) => (
                          <tr key={rowIdx}>
                            {row.map((cell, cellIdx) => (
                              <td key={cellIdx} className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">Error</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Import Results */}
          {currentStep === 3 && importResult && (
            <div className="space-y-6">
              {/* Summary */}
              <div className={`border rounded-lg p-6 ${importResult.success_count > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-start gap-4">
                  {importResult.success_count > 0 ? (
                    <CheckCircleIcon className="w-8 h-8 text-green-600 flex-shrink-0" />
                  ) : (
                    <XCircleIcon className="w-8 h-8 text-red-600 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {importResult.success_count > 0 ? 'Import Completed' : 'Import Failed'}
                    </h3>
                    <div className="space-y-1 text-sm text-gray-700">
                      <p>Total rows: <strong>{importResult.total_rows}</strong></p>
                      <p className="text-green-700">Successfully imported: <strong>{importResult.success_count}</strong></p>
                      {importResult.failed_count > 0 && (
                        <p className="text-red-700">Failed: <strong>{importResult.failed_count}</strong></p>
                      )}
                      {importResult.auto_corrected_count > 0 && (
                        <p className="text-amber-600">
                          <span className="inline-flex items-center gap-1">
                            Auto-corrected: <strong>{importResult.auto_corrected_count}</strong>
                            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">Template IDs cleaned</span>
                          </span>
                        </p>
                      )}
                      {importResult.reference_number && (
                        <p className="text-blue-700 font-medium">
                          <span className="inline-flex items-center gap-1">
                            Reference No: <strong>{importResult.reference_number}</strong>
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Support Reference</span>
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Auto-corrections */}
              {importResult.auto_corrected_count > 0 && importResult.auto_corrected_rows && importResult.auto_corrected_rows.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Auto-Correction Details
                    <span className="ml-2 text-sm font-normal text-amber-600">
                      Template IDs automatically cleaned
                    </span>
                  </h3>
                  <div className="border border-amber-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-amber-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Row
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Original ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cleaned ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {importResult.auto_corrected_rows.map((correction, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {correction.row}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <span className="line-through">{correction.original_id}</span>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-green-700">
                              {correction.corrected_id}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                                Auto-corrected
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors */}
              {importResult.errors && importResult.errors.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Error Details</h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Row
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reason
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {importResult.errors.map((err, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {err.row}
                            </td>
                            <td className="px-4 py-3 text-sm text-red-600">
                              {err.reason}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div>
            {currentStep === 2 && (
              <button
                onClick={() => setCurrentStep(1)}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {currentStep < 3 && (
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
              >
                Cancel
              </button>
            )}
            
            {currentStep === 1 && (
              <button
                onClick={handleParseFile}
                disabled={!file || !selectedEntityId || uploading}
                className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Parsing...
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRightIcon className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
            
            {currentStep === 2 && (
              <button
                onClick={handleImport}
                disabled={importing}
                className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    Start Import
                    <ArrowUpTrayIcon className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
            
            {currentStep === 3 && (
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadWizard;
